import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import reportService, { type UserReportItem } from "@/services/report";
import userService, { type User } from "@/services/user";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type UserReportTab = "sale" | "payment" | "quotation";

type UserReportFilters = {
  user_id: number | null;
  warehouse_id: number | null;
  from_date: string;
  to_date: string;
};

type TabState = {
  items: UserReportItem[];
  loading: boolean;
  currentPage: number;
  total: number;
  perPage: number;
};

const createInitialFilters = (): UserReportFilters => ({
  user_id: null,
  warehouse_id: null,
  from_date: "",
  to_date: "",
});

const createTabState = (): TabState => ({
  items: [],
  loading: false,
  currentPage: 1,
  total: 0,
  perPage: 10,
});

export function useUserReport() {
  const { t } = useI18n();
  const activeTab = ref<UserReportTab>("sale");
  const users = ref<User[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const searchQuery = ref("");
  const filters = reactive<UserReportFilters>(createInitialFilters());
  const i18nScope = "user_report_page";
  const pageTitle = `${i18nScope}.title`;

  const saleState = reactive<TabState>(createTabState());
  const paymentState = reactive<TabState>(createTabState());
  const quotationState = reactive<TabState>(createTabState());

  const currentState = computed(() => {
    if (activeTab.value === "payment") return paymentState;
    if (activeTab.value === "quotation") return quotationState;
    return saleState;
  });

  const pagination = computed(() => ({
    current: currentState.value.currentPage,
    pageSize: currentState.value.perPage,
    total: currentState.value.total,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      t(`${i18nScope}.pagination_total`, { start: range[0], end: range[1], total }),
    locale: { items_per_page: t(`${i18nScope}.items_per_page`) },
  }));

  const summary = computed(() => {
    const items = currentState.value.items;

    if (activeTab.value === "payment") {
      const totalAmount = items.reduce((sum, item) => sum + Number((item as unknown as { amount?: number }).amount ?? 0), 0);
      return { label: t(`${i18nScope}.summary_payment_total`), total: totalAmount };
    }

    const grandTotal = items.reduce((sum, item) => sum + Number(item.invoice_total ?? 0), 0);
    const paid = items.reduce((sum, item) => sum + Number(item.paid ?? 0), 0);
    const due = items.reduce((sum, item) => sum + Number(item.due ?? 0), 0);

    return { label: t(`${i18nScope}.summary_grand_total`), total: grandTotal, paid, due };
  });

  const queryParams = (tab: UserReportTab, state: TabState) => ({
    user_id: filters.user_id || undefined,
    warehouse_id: filters.warehouse_id || undefined,
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
    search_query: searchQuery.value || undefined,
    report_type: tab,
    page: state.currentPage,
    per_page: state.perPage,
  });

  const fetchFilterOptions = async () => {
    try {
      const [userResults, warehouseResults] = await Promise.all([
        userService.getUsers({ per_page: 1000 }),
        warehouseService.getWarehouses(),
      ]);

      users.value = userResults;
      warehouses.value = warehouseResults;
    } catch (error) {
      console.error("Error loading user report filters:", error);
    }
  };

  const fetchTab = async (tab: UserReportTab, state: TabState) => {
    state.loading = true;

    try {
      const response = await reportService.getUserReports(queryParams(tab, state));
      state.items = response.data ?? [];
      state.currentPage = response.current_page;
      state.total = response.total;
    } catch (error) {
      console.error(`Error fetching ${tab} user report:`, error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.load_failed`),
        text: t(`${i18nScope}.unable_to_load_report`),
      });
    } finally {
      state.loading = false;
    }
  };

  const fetchActiveTab = async () => {
    if (activeTab.value === "payment") {
      await fetchTab("payment", paymentState);
      return;
    }

    if (activeTab.value === "quotation") {
      await fetchTab("quotation", quotationState);
      return;
    }

    await fetchTab("sale", saleState);
  };

  const setActiveTab = (tab: UserReportTab) => {
    if (activeTab.value === tab) return;
    activeTab.value = tab;
    fetchActiveTab();
  };

  const handleTableChange = (page: number, pageSize: number) => {
    const state = currentState.value;
    state.currentPage = page;

    if (pageSize !== state.perPage) {
      state.perPage = pageSize;
      state.currentPage = 1;
    }

    fetchActiveTab();
  };

  const applyFilters = () => {
    saleState.currentPage = 1;
    paymentState.currentPage = 1;
    quotationState.currentPage = 1;
    fetchActiveTab();
  };

  const resetFilters = () => {
    Object.assign(filters, createInitialFilters());
    searchQuery.value = "";
    saleState.currentPage = 1;
    paymentState.currentPage = 1;
    quotationState.currentPage = 1;
    fetchActiveTab();
  };

  onMounted(async () => {
    await fetchFilterOptions();
    await fetchActiveTab();
  });

  watch(searchQuery, () => {
    saleState.currentPage = 1;
    paymentState.currentPage = 1;
    quotationState.currentPage = 1;
    fetchActiveTab();
  });

  return {
    pageTitle,
    i18nScope,
    activeTab,
    users,
    warehouses,
    searchQuery,
    filters,
    saleState,
    paymentState,
    quotationState,
    currentState,
    pagination,
    summary,
    setActiveTab,
    handleTableChange,
    applyFilters,
    resetFilters,
    fetchActiveTab,
  };
}
